import { useEffect, useState } from "react";
import {
  Send, Play, Pause, CheckCircle2, Clock, MapPin, Calendar as CalIcon,
  Mail, AlertTriangle, Loader2, ChevronRight, Plus, Trash2,
} from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

/**
 * Section Campagnes de rappels — Admin.
 * Workflow conforme au cahier des charges Gloire Ngebe :
 *  1. Créer (ou ouvrir) une campagne attachée à un événement
 *  2. Tester (envoie les 6 templates à mon email uniquement)
 *  3. Activer manuellement → le scheduler enverra J-5..J-J automatiquement
 *  4. Pause possible à tout moment
 */
export default function CampaignsAdmin() {
  const [steps, setSteps] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [events, setEvents] = useState([]);
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState(null); // action en cours
  const [showCreate, setShowCreate] = useState(false);

  const refresh = () => api.get("/admin/campaigns").then((r) => setCampaigns(r.data));

  useEffect(() => {
    api.get("/admin/campaigns/steps").then((r) => setSteps(r.data));
    api.get("/admin/events").then((r) => setEvents(r.data));
    refresh();
  }, []);

  const create = async (form) => {
    setBusy("create");
    try {
      const r = await api.post("/admin/campaigns", form);
      toast.success("Campagne créée. Pas encore d'envoi tant que vous n'activez pas.");
      setSelected(r.data);
      setShowCreate(false);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally { setBusy(null); }
  };

  const sendTest = async (c) => {
    if (!confirm("Envoyer les 6 emails de test à votre adresse admin ?\n\nAucun email ne sera envoyé aux participants.")) return;
    setBusy("test");
    try {
      const r = await api.post(`/admin/campaigns/${c.id}/test`);
      toast.success(`${r.data.sent} emails de test envoyés à votre adresse`);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally { setBusy(null); }
  };

  const activate = async (c) => {
    if (!confirm(`⚠️ ATTENTION\n\nVous allez ACTIVER la campagne automatique. Tous les inscrits non-révoqués recevront les emails de rappel selon le calendrier J-5 à J-J.\n\nÊtes-vous certain·e d'avoir testé avant ?`)) return;
    setBusy("activate");
    try {
      await api.post(`/admin/campaigns/${c.id}/activate`);
      toast.success("Campagne ACTIVE — les rappels seront envoyés automatiquement");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally { setBusy(null); }
  };

  const pause = async (c) => {
    setBusy("pause");
    try {
      await api.post(`/admin/campaigns/${c.id}/pause`);
      toast.success("Campagne mise en pause");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally { setBusy(null); }
  };

  const sendStepNow = async (c, stepKey) => {
    if (!confirm(`Envoyer le palier ${stepKey.toUpperCase()} maintenant à tous les inscrits non-révoqués ?\n\n(Idempotent : ceux déjà destinataires de ce palier seront ignorés.)`)) return;
    setBusy("step-" + stepKey);
    try {
      const r = await api.post(`/admin/campaigns/${c.id}/send-step/${stepKey}`);
      toast.success(`${r.data.sent} envoyés · ${r.data.skipped} ignorés · ${r.data.failed} échecs`);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally { setBusy(null); }
  };

  return (
    <div data-testid="campaigns-admin">
      <div className="flex items-start justify-between gap-3 mb-6">
        <div>
          <div className="eyebrow text-gold-dark">Communication</div>
          <h2 className="font-serif text-2xl text-ink mt-1">Campagnes de rappels</h2>
          <p className="text-xs text-foreground/55 mt-1 max-w-2xl">
            Programmation automatique d'emails J-5 → J-J pour vos événements. Chaque jour
            a un thème différent (confirmation, intervenants, enjeux, accès, dernier rappel, jour J).
          </p>
        </div>
        <button onClick={() => setShowCreate(true)} className="btn-primary text-sm" data-testid="new-campaign-btn">
          <Plus className="w-4 h-4" /> Nouvelle campagne
        </button>
      </div>

      {/* List */}
      {campaigns.length === 0 ? (
        <div className="bg-white border border-border rounded-sm p-10 text-center">
          <Mail className="w-10 h-10 mx-auto text-gold-dark" />
          <h3 className="font-serif text-xl text-ink mt-3">Aucune campagne</h3>
          <p className="text-sm text-foreground/55 mt-2">Créez votre première campagne pour la Croisade Scientifique.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {campaigns.map((c) => (
            <CampaignCard key={c.id} c={c} steps={steps} busy={busy}
                          onTest={sendTest} onActivate={activate} onPause={pause} onSendStep={sendStepNow}
                          isOpen={selected?.id === c.id} onToggle={() => setSelected(selected?.id === c.id ? null : c)} />
          ))}
        </div>
      )}

      {showCreate && (
        <CreateCampaignModal events={events} onClose={() => setShowCreate(false)} onSubmit={create} busy={busy === "create"} />
      )}
    </div>
  );
}

function CampaignCard({ c, steps, busy, onTest, onActivate, onPause, onSendStep, isOpen, onToggle }) {
  const STATUS = {
    created: { c: "bg-foreground/10 text-foreground/70", l: "Brouillon" },
    testing: { c: "bg-blue-100 text-blue-700", l: "Tests envoyés" },
    active: { c: "bg-emerald-100 text-emerald-700", l: "ACTIVE" },
    paused: { c: "bg-amber-100 text-amber-800", l: "En pause" },
    completed: { c: "bg-foreground/10 text-foreground/55", l: "Terminée" },
  }[c.status] || { c: "bg-foreground/10", l: c.status };

  const sentByStep = (c.sent_log || []).reduce((acc, e) => {
    if (!e.ok) return acc;
    acc[e.step] = (acc[e.step] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="bg-white border border-border rounded-sm overflow-hidden" data-testid={`campaign-${c.id}`}>
      <div className="p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <button onClick={onToggle} className="flex-1 text-left flex items-center gap-3">
          <ChevronRight className={`w-4 h-4 text-foreground/40 transition-transform ${isOpen ? "rotate-90" : ""}`} />
          <div className="flex-1">
            <div className="font-serif text-ink text-lg">{c.event_title}</div>
            <div className="text-xs text-foreground/60 mt-1 flex items-center gap-3 flex-wrap">
              <span className="inline-flex items-center gap-1.5"><CalIcon className="w-3 h-3" /> {c.event_date}</span>
              <span className="inline-flex items-center gap-1.5"><MapPin className="w-3 h-3" /> {c.event_address}</span>
            </div>
          </div>
          <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-sm text-[0.65rem] uppercase tracking-wider font-bold ${STATUS.c}`}>{STATUS.l}</span>
        </button>

        <div className="flex items-center gap-2 shrink-0">
          {c.status !== "active" && c.status !== "completed" && (
            <button disabled={busy === "test"} onClick={() => onTest(c)} className="btn-outline text-xs" data-testid="campaign-test-btn">
              {busy === "test" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
              Envoyer test (admin)
            </button>
          )}
          {c.status === "active" ? (
            <button disabled={busy === "pause"} onClick={() => onPause(c)} className="px-3 py-2 bg-amber-500 text-white text-xs font-bold rounded-sm hover:bg-amber-600 inline-flex items-center gap-1.5" data-testid="campaign-pause-btn">
              <Pause className="w-3.5 h-3.5" /> Pause
            </button>
          ) : c.status === "paused" ? (
            <button disabled={busy === "activate"} onClick={() => onActivate(c)} className="px-3 py-2 bg-emerald-600 text-white text-xs font-bold rounded-sm hover:bg-emerald-700 inline-flex items-center gap-1.5" data-testid="campaign-resume-btn">
              <Play className="w-3.5 h-3.5" /> Reprendre
            </button>
          ) : c.status !== "completed" && (
            <button disabled={busy === "activate"} onClick={() => onActivate(c)} className="px-3 py-2 bg-emerald-600 text-white text-xs font-bold rounded-sm hover:bg-emerald-700 inline-flex items-center gap-1.5" data-testid="campaign-activate-btn">
              {busy === "activate" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Play className="w-3.5 h-3.5" />}
              Activer la campagne
            </button>
          )}
        </div>
      </div>

      {isOpen && (
        <div className="border-t border-border p-5 bg-bone/30">
          {c.status !== "active" && c.status !== "testing" && c.status !== "paused" && c.status !== "completed" && (
            <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-sm flex gap-2 text-xs">
              <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
              <span className="text-amber-900">
                <strong>Mode brouillon</strong> — Aucun email ne sera envoyé aux participants tant que vous n'avez pas
                cliqué sur "Activer la campagne". Commencez par "Envoyer test" pour recevoir les 6 templates à votre adresse admin.
              </span>
            </div>
          )}
          {c.status === "active" && (
            <div className="mb-4 p-3 bg-emerald-50 border border-emerald-200 rounded-sm flex gap-2 text-xs">
              <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
              <span className="text-emerald-900">
                <strong>Campagne active</strong> — Le scheduler envoie automatiquement chaque palier à J-5, J-4, J-3, J-2, J-1 et J-0 selon la date de l'événement ({c.event_date}). Les envois sont idempotents (jamais de doublon).
              </span>
            </div>
          )}

          <div className="eyebrow text-foreground/55 text-[0.6rem] mb-2 flex items-center gap-1.5">
            <Clock className="w-3 h-3" /> Calendrier des 6 paliers
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {steps.map((s) => {
              const n = sentByStep[s.key] || 0;
              return (
                <div key={s.key} className="bg-white border border-border p-3 rounded-sm">
                  <div className="flex items-center justify-between">
                    <div className="text-[0.65rem] uppercase tracking-[0.2em] font-bold text-gold-dark">{s.label}</div>
                    {n > 0 && <span className="text-[0.65rem] bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded">{n} envoyé{n > 1 ? "s" : ""}</span>}
                  </div>
                  <div className="font-serif text-ink text-sm mt-1 leading-tight">{s.title}</div>
                  <div className="text-[0.7rem] text-foreground/55 mt-1 line-clamp-2">{s.subject.replace("{event_title}", c.event_title)}</div>
                  {c.status === "active" && (
                    <button disabled={busy === "step-" + s.key} onClick={() => onSendStep(c, s.key)}
                            className="mt-2 text-[0.65rem] text-foreground/55 hover:text-ink inline-flex items-center gap-1"
                            data-testid={`send-step-${s.key}`}>
                      {busy === "step-" + s.key ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
                      Forcer envoi maintenant
                    </button>
                  )}
                </div>
              );
            })}
          </div>

          {c.sent_log && c.sent_log.length > 0 && (
            <details className="mt-5">
              <summary className="cursor-pointer text-sm font-semibold text-foreground/75">
                Journal des envois ({c.sent_log.length})
              </summary>
              <div className="mt-3 max-h-72 overflow-y-auto bg-white border border-border rounded-sm">
                <table className="w-full text-xs">
                  <thead className="bg-bone text-foreground/55 sticky top-0">
                    <tr>
                      <th className="px-3 py-1.5 text-left">Date</th>
                      <th className="px-3 py-1.5 text-left">Palier</th>
                      <th className="px-3 py-1.5 text-left">N°</th>
                      <th className="px-3 py-1.5 text-left">Email</th>
                      <th className="px-3 py-1.5 text-left">Statut</th>
                    </tr>
                  </thead>
                  <tbody>
                    {c.sent_log.slice().reverse().slice(0, 50).map((e, i) => (
                      <tr key={i} className="border-t border-border">
                        <td className="px-3 py-1.5">{new Date(e.at).toLocaleString("fr-FR")}</td>
                        <td className="px-3 py-1.5 font-mono">{e.step}</td>
                        <td className="px-3 py-1.5 font-mono">{e.registration_number}</td>
                        <td className="px-3 py-1.5">{e.email}</td>
                        <td className="px-3 py-1.5">{e.ok ? <span className="text-emerald-600">✓</span> : <span className="text-crimson">✗ {e.reason?.slice(0, 30)}</span>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </details>
          )}
        </div>
      )}
    </div>
  );
}

function CreateCampaignModal({ events, onClose, onSubmit, busy }) {
  const [f, setF] = useState({
    event_id: events.find((e) => e.is_featured)?.id || events[0]?.id || "",
    event_date: "2026-06-20",
    event_time: "14h00 — 18h00",
    event_address: "10-18 Rue des Terres au Curé, 75013 Paris",
    practical_info: "<strong>Métro</strong>&nbsp;: Ligne 14 (Olympiades) · Ligne 7 (Porte d'Ivry)<br/><strong>Tramway</strong>&nbsp;: T3a (Maryse Bastié · Porte d'Ivry)<br/><strong>Bus</strong>&nbsp;: 27, 83, 62 (arrêt Patay-Tolbiac)<br/><strong>Voiture</strong>&nbsp;: parking à proximité, accès par le périphérique",
  });

  return (
    <div className="fixed inset-0 z-[80] grid place-items-center p-4 sm:p-6" onClick={onClose}>
      <div className="absolute inset-0 bg-ink/80 backdrop-blur-sm" />
      <div className="relative bg-white w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-sm shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="p-7">
          <div className="eyebrow text-gold-dark">Nouvelle campagne</div>
          <h2 className="font-serif text-2xl text-ink mt-1">Informations pratiques de l'événement</h2>
          <p className="text-sm text-foreground/60 mt-2">Ces informations seront utilisées dans les emails J-2 et J-1. Aucun email ne sera envoyé tant que vous n'aurez pas activé la campagne.</p>

          <div className="mt-5 space-y-4">
            <Field label="Événement">
              <select value={f.event_id} onChange={(e) => setF({ ...f, event_id: e.target.value })}
                      data-testid="create-event-id"
                      className="w-full border border-border rounded-sm p-2.5 text-sm bg-white">
                {events.map((e) => <option key={e.id} value={e.id}>{e.title}</option>)}
              </select>
            </Field>
            <div className="grid sm:grid-cols-2 gap-3">
              <Field label="Date">
                <input type="date" value={f.event_date} onChange={(e) => setF({ ...f, event_date: e.target.value })} data-testid="create-date"
                       className="w-full border border-border rounded-sm p-2.5 text-sm" />
              </Field>
              <Field label="Horaire">
                <input value={f.event_time} onChange={(e) => setF({ ...f, event_time: e.target.value })} data-testid="create-time"
                       className="w-full border border-border rounded-sm p-2.5 text-sm" />
              </Field>
            </div>
            <Field label="Adresse">
              <input value={f.event_address} onChange={(e) => setF({ ...f, event_address: e.target.value })} data-testid="create-address"
                     className="w-full border border-border rounded-sm p-2.5 text-sm" />
            </Field>
            <Field label="Infos d'accès (HTML)">
              <textarea value={f.practical_info} onChange={(e) => setF({ ...f, practical_info: e.target.value })} rows={5} data-testid="create-practical"
                        className="w-full border border-border rounded-sm p-2.5 text-sm font-mono" />
              <div className="text-[0.65rem] text-foreground/50 mt-1">Vous pouvez utiliser HTML (&lt;strong&gt;, &lt;br/&gt;).</div>
            </Field>
          </div>

          <div className="mt-6 flex justify-end gap-2">
            <button onClick={onClose} className="btn-outline text-sm">Annuler</button>
            <button disabled={busy} onClick={() => onSubmit(f)} className="btn-primary text-sm" data-testid="create-submit">
              {busy ? "…" : "Créer la campagne"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div className="text-[0.6rem] uppercase tracking-[0.18em] font-semibold text-foreground/55 mb-1.5">{label}</div>
      {children}
    </div>
  );
}
